import cn from 'classnames'
import Modal from '@/UI/Modals/Modal'

import GrainButton from '@/shared/GrainButton'
import { FC, ReactElement, ReactNode } from 'react'
import { ArrowSVGIcon, CloseSVGIcon } from '@/components/icons'

type ModalFooterProps = {
  icon?: ReactElement,
  onClick: () => void,
  className?: string,
  isLoading?: boolean,
  buttonText?: ReactNode,
  isDisabled?: boolean,
  secondaryText?: ReactNode,
  secondaryOnClick?: () => void,
  buttonClassName?: string,
}

export const ModalFooter:FC<ModalFooterProps> = ({
  icon,
  onClick,
  className,
  isLoading,
  buttonText = 'Save Changes',
  isDisabled,
  secondaryText,
  secondaryOnClick,
  buttonClassName,
}) => (
  // `.modal-content .button-row-footer` (0-2-0; `!important` as in the SCSS), keyed on the class
  // token as the selector was; `fullWidth` below keeps its exact-string test.
  <Modal.Footer className={cn(className, className?.split(/\s+/).includes('button-row-footer') && '[.modal-content_&]:!flex-row-reverse [.modal-content_&]:justify-between [.modal-content_&_button+button]:mt-0')}>
    <GrainButton
      icon={icon}
      variant="primary"
      text={buttonText}
      fullWidth={className !== 'button-row-footer'}
      onClick={onClick}
      isDisabled={isDisabled}
      isLoading={isLoading}
      className={buttonClassName}
    />
    { secondaryOnClick && (
      <GrainButton
        variant="tertiary"
        text={secondaryText}
        fullWidth={className !== 'button-row-footer'}
        onClick={secondaryOnClick}
      />
    )}
  </Modal.Footer>
)

type ModalBodyProps = {
  children: ReactNode,
  className?: string,
}

export const ModalBody:FC<ModalBodyProps> = ({ children, className }) => (
  <Modal.Body className={className}>
    { children }
  </Modal.Body>
)

type ModalHeaderProps = {
  headerText: string,
  onClose?: () => void,
  onBackClick?: () => void,
}

export const ModalHeader:FC<ModalHeaderProps> = ({ headerText, onClose, onBackClick }) => (
  <Modal.Header>
    { onBackClick && (
      <GrainButton
        icon={<ArrowSVGIcon />}
        type="button"
        variant="tertiary"
        onClick={onBackClick}
        // Ancestors spelled out as in the SCSS: only a modal header positions these, at 0-3-0 / 0-3-1.
        className="back-button [.modal-content_.modal-header_&]:absolute [.modal-content_.modal-header_&]:left-[16px] [.modal-content_.modal-header_&]:top-[16px] [.modal-content_.modal-header_&_svg]:h-[16px] [.modal-content_.modal-header_&_svg]:w-[17px] [.modal-content_.modal-header_&_svg_path]:stroke-gray-1"
      />
    )}
    <h1>{ headerText }</h1>
    { onClose && (
      <GrainButton
        icon={<CloseSVGIcon />}
        type="button"
        variant="tertiary"
        onClick={onClose}
        className="close-button [.modal-content_.modal-header_&]:absolute [.modal-content_.modal-header_&]:right-[16px] [.modal-content_.modal-header_&]:top-[16px] [.modal-content_.modal-header_&]:w-[32px] [.modal-content_.modal-header_&]:rounded-[50%] [.modal-content_.modal-header_&]:bg-gray-6 [.modal-content_.modal-header_&_svg]:h-[10px] [.modal-content_.modal-header_&_svg]:w-[10px]"
      />
    )}
  </Modal.Header>
)

type ModalFrameProps = {
  show: boolean,
  onHide?: () => void,
  children: ReactNode,
  dialogClassName?: string,
  contentClassName?: string,
  animation?: boolean,
  backdrop?: boolean | 'static',
  keyboard?: boolean,
  labelledBy?: string,
}

export const ModalFrame:FC<ModalFrameProps> = ({
  show, onHide, children, dialogClassName = 'standard-dialog w-[375px]', contentClassName, animation = true, backdrop = 'static', keyboard = true, labelledBy,
}) => (
  <Modal
    aria-labelledby={labelledBy}
    show={show}
    onHide={onHide}
    keyboard={keyboard}
    centered
    dialogClassName={dialogClassName}
    contentClassName={contentClassName}
    animation={animation}
    backdrop={backdrop}
  >
    { children }
  </Modal>
)
